import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../utils/api";

/**
 * Fetch page skeleton data (lightweight structure without heavy content)
 * @param {number} websiteId - The website ID
 * @param {string} subdomain - The page subdomain (optional)
 * @returns {object} React Query result with page skeleton data
 */
export const usePageSkeleton = (websiteId, subdomain) => {
  return useQuery({
    queryKey: ["page-skeleton", websiteId, subdomain || "main"],
    queryFn: async () => {
      const url = subdomain && subdomain !== "undefined"
        ? `/api/websites/${websiteId}/pages/${subdomain}/`
        : `/api/websites/${websiteId}/pages/`;
      
      const response = await apiFetch(url);
      if (!response.ok) {
        const contentType = response.headers.get("content-type") || "";
        const data = contentType.includes("application/json")
          ? await response.json()
          : null;
        throw new Error(data?.error || "Page not found");
      }
      
      const data = await response.json();
      // Handle both array and single object responses
      const pageData = Array.isArray(data)
        ? data.find((p) => !p.subdomain) || data[0] || null
        : data;
      
      if (!pageData) {
        throw new Error("Page not found");
      }
      
      return pageData;
    },
    staleTime: Infinity, // Skeleton structure rarely changes
    enabled: !!websiteId, // Only run if websiteId exists
  });
};

/**
 * Fetch individual component data with styling and structure
 * @param {number} componentId - The component ID
 * @param {object} options - Additional options
 * @returns {object} React Query result with component data
 */
export const useComponent = (componentId, options = {}) => {
  return useQuery({
    queryKey: ["component", componentId],
    queryFn: async () => {
      const response = await apiFetch(`/api/components/${componentId}`);
      if (!response.ok) {
        throw new Error(`Error fetching component: ${response.status}`);
      }
      return response.json();
    },
    staleTime: 30 * 1000, // 30 seconds
    enabled: !!componentId,
    ...options,
  });
};

/**
 * Fetch individual item data
 * @param {number} itemId - The item ID
 * @param {object} options - Additional options
 * @returns {object} React Query result with item data
 */
export const useItem = (itemId, options = {}) => {
  return useQuery({
    queryKey: ["item", itemId],
    queryFn: async () => {
      const response = await apiFetch(`/api/items/${itemId}`);
      if (!response.ok) {
        throw new Error(`Error fetching item: ${response.status}`);
      }
      return response.json();
    },
    staleTime: 30 * 1000, // 30 seconds
    enabled: !!itemId,
    ...options,
  });
};

/**
 * Bulk fetch multiple items at once
 * @param {number[]} itemIds - Array of item IDs
 * @param {object} options - Additional options
 * @returns {object} React Query result with bulk item data
 */
export const useItemsBulk = (itemIds = [], options = {}) => {
  const sortedIds = itemIds.sort().join(',');
  
  return useQuery({
    queryKey: ["items-bulk", sortedIds],
    queryFn: async () => {
      if (itemIds.length === 0) return [];
      
      const response = await apiFetch(`/api/items/bulk/?ids=${itemIds.join(',')}`);
      if (!response.ok) {
        throw new Error(`Error fetching items: ${response.status}`);
      }
      return response.json();
    },
    staleTime: 30 * 1000, // 30 seconds
    enabled: itemIds.length > 0,
    ...options,
  });
};
